import React, { useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Award, Users, Zap, TrendingUp } from 'lucide-react';
import { stats, whyHowzTech } from '../data/mock';

const AnimatedCounter = ({ value, suffix, inView }) => {
  const [count, setCount] = React.useState(0);

  useEffect(() => {
    if (!inView) return;
    
    let startTime;
    const duration = 2000;
    const startValue = 0;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * value));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, inView]);

  return (
    <span className="text-5xl md:text-6xl font-bold text-[#00FFD1]">
      {count}{suffix}
    </span>
  );
};

const WhyHowzTech = () => {
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true, margin: "-100px" });

  const iconMap = [
    { icon: TrendingUp, gradient: 'from-[#00FFD1] to-cyan-400' },
    { icon: Users, gradient: 'from-[#00FFD1] to-emerald-400' },
    { icon: Zap, gradient: 'from-[#00FFD1] to-blue-400' },
    { icon: Award, gradient: 'from-[#00FFD1] to-teal-400' }
  ];

  return (
    <section id="about" className="relative py-32 bg-[#121212] overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#00FFD1]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#00FFD1]/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block text-[#00FFD1] text-lg font-medium mb-4 tracking-wide"
          >
            WHY CHOOSE US
          </motion.span>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Why <span className="text-[#00FFD1]">HowzTech</span>?
          </h2>
          <p className="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed">
            Your trusted partner for digital transformation and technological excellence
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          ref={statsRef}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-24"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center p-8 bg-black/50 border border-white/25 backdrop-blur-sm transition-all duration-400 hover:border-[#00FFD1] hover:shadow-[0_0_30px_rgba(0,255,209,0.2)]"
            >
              <AnimatedCounter 
                value={stat.value} 
                suffix={stat.suffix} 
                inView={isStatsInView}
              />
              <p className="text-white/85 text-lg mt-4 font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Why HowzTech Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {whyHowzTech.map((item, index) => {
            const { icon: IconComponent, gradient } = iconMap[index];
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative bg-black p-8 border border-white/25 transition-all duration-400 hover:border-[#00FFD1] overflow-hidden"
              >
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#00FFD1]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />
                
                <div className="relative z-10 flex gap-6">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br ${gradient} p-0.5 transition-transform duration-400 group-hover:scale-110`}>
                    <div className="w-full h-full bg-black flex items-center justify-center">
                      <IconComponent className="text-[#00FFD1]" size={28} strokeWidth={1.5} />
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-white mb-3 leading-tight group-hover:text-[#00FFD1] transition-colors duration-400">
                      {item.title}
                    </h3>
                    <p className="text-white/85 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyHowzTech;
